<template>
  <span :class="{ highlight: highlight(product.categoria, product.group_code ?? product.codice, feature) }">
    <template v-if="feature === 'spazio'">
      <template v-if="product.spazio === 0"> {{ $t('All-flash Storage illimitato') }}</template>
      <template v-else-if="product.spazio"> {{ $size(product.spazio * 1000000) }} {{ $t('All-flash Storage') }}</template>
    </template>

    <template v-else-if="feature === 'traffico'">
      <span v-if="product.traffico === 0"> {{ $t('Traffico illimitato') }}</span>
      <span v-else-if="product.traffico">
        <template v-if="showLabel">{{ $t('Traffico') }}:</template> {{ $size(product.traffico * 1000000) }}</span
      >
    </template>

    <span v-else-if="feature === 'database' && product.database">
      <template v-if="showLabel">{{ $t('Database') }}{{ ' ' }}</template
      >{{ $t('illimitati') }}
    </span>

    <span v-else-if="feature === 'inode' && product.inode">
      <template v-if="showLabel">{{ $t('Inode') }}: </template>{{ $t('illimitati') }}
    </span>

    <template v-else-if="feature === 'nr_domini'">
      {{ $t('Multidominio') }}:
      <span v-if="product.nr_domini === 0">{{ $t('No') }}</span>
      <span v-else class="capitalized">{{ $t('{0} domini', [product.nr_domini]) }}</span>
    </template>

    <span v-else-if="feature === 'ip_dedicato' && product.ip_dedicato">
      <template v-if="showLabel">{{ $t('IP Dedicato') }}: </template>{{ product.ip_dedicato }}
    </span>

    <span v-else-if="feature === 'monitoring' && product.monitoring">
      <template v-if="showLabel">{{ $t('Monitoring') }}: </template>{{ product.monitoring }}
      <form-help-tooltip :text="monitoring[product.monitoring]" />
    </span>

    <span v-else-if="feature === 'support' && product.support">
      <template v-if="showLabel">{{ $t('Supporto') }}: </template>{{ product.support }}
      <form-help-tooltip :text="support[product.support]" />
    </span>

    <span v-else-if="feature === 'utenti'">
      <template v-if="showLabel">{{ $t('Utenti') }}: </template><template v-if="product.utenti === 0">{{ $t('illimitati') }}</template
      ><template v-else>{{ product.utenti }}</template>
    </span>

    <with-var v-else :value="allFeatures[feature]" #="{ var: featDef }">
      <span :title="featDef.label" :class="{ capitalized: featDef.capitalized }">
        <template v-if="featDef.showLabel && showLabel">{{ featDef.label }}:{{ ' ' }}</template>

        <template v-if="typeof product[feature] === 'boolean'">
          <template v-if="showLabel && product[feature]">{{ featDef.label }}{{ ' ' }}</template>
          <template v-else>
            <circle-check-icon v-if="product[feature]" color="green" />
            <xmark-icon v-else color="red" />
          </template>
        </template>
        <template v-else-if="product[feature]">
          {{ product[feature] }} <template v-if="featDef.um">{{ featDef.um }}</template>
        </template>
        <xmark-icon v-else color="red" />

        <form-help-tooltip v-if="showLabel && featDef.tooltip" :text="featDef.tooltip" />
        <template v-if="featDef.description">{{ featDef.description }}</template>
      </span>
    </with-var>
  </span>
</template>

<style lang="scss" scoped>
span {
  white-space: pre-wrap;
}

strong {
  font-size: inherit;
}

.pficon,
.fa {
  margin-right: 5px;
  width: 20px;
}

:deep(svg) {
  margin: 2px;
}

.capitalized {
  text-transform: capitalize;
}

.highlight {
  font-weight: bold;
  color: #ec7a08;
}
</style>

<script lang="ts">
import CpuIcon from '@vue-patternfly/icons/cpu-icon';
import MicrochipIcon from '@vue-patternfly/icons/microchip-icon';
import MemoryIcon from '@vue-patternfly/icons/memory-icon';
import VolumeIcon from '@vue-patternfly/icons/volume-icon';
import TrendUpIcon from '@vue-patternfly/icons/trend-up-icon';
import ApplicationsIcon from '@vue-patternfly/icons/applications-icon';
import VirtualMachineIcon from '@vue-patternfly/icons/virtual-machine-icon';
import EnvelopeIcon from '@vue-patternfly/icons/envelope-icon';
import GlobeIcon from '@vue-patternfly/icons/globe-icon';
import DatabaseIcon from '@vue-patternfly/icons/database-icon';
import NetworkIcon from '@vue-patternfly/icons/network-icon';
import ArrowsRotateIcon from '@vue-patternfly/icons/arrows-rotate-icon';
import AddressCardIcon from '@vue-patternfly/icons/address-card-icon';
import NetworkWiredIcon from '@vue-patternfly/icons/network-wired-icon';
import ArrowIcon from '@vue-patternfly/icons/arrow-icon';
import LockIcon from '@vue-patternfly/icons/lock-icon';
import GaugeIcon from '@vue-patternfly/icons/gauge-icon';
import EyeIcon from '@vue-patternfly/icons/eye-icon';
import UsersIcon from '@vue-patternfly/icons/users-icon';
import CircleCheckIcon from '@vue-patternfly/icons/circle-check-icon';
import XmarkIcon from '@vue-patternfly/icons/xmark-icon';

export type Feature = {
  [K in keyof ProductFeatures]: {
    label: string;
    icon?: any; //TODO togliere any e mettere "componente" ?
    um?: string;
    description?: string;
    capitalized?: boolean;
    tooltip?: string;
    showLabel?: boolean;
    highlight?: boolean;
  };
};

export const getAllProductFeatures = (): Feature => ({
  descrizione: {
    label: _t('Descrizione'),
  },
  spazio: {
    label: _t('Storage'),
    icon: VolumeIcon,
    um: 'GB All-flash Storage',
  },
  traffico: {
    label: _t('Traffico Mensile'),
    icon: TrendUpIcon,
    um: 'GB Traffico',
  },
  gpu: {
    label: _t('GPU'),
    icon: MicrochipIcon,
    showLabel: true,
  },
  gpu_ram: {
    label: _t('GPU RAM'),
    icon: MicrochipIcon,
    showLabel: true,
    um: 'GB',
  },
  core: {
    label: _t('Core'),
    icon: CpuIcon,
    um: 'Core',
  },
  ram: {
    label: _t('RAM'),
    icon: MemoryIcon,
    um: 'GB',
    showLabel: true,
  },
  ip_dedicato: {
    label: _t('Ip dedicato'),
    icon: ArrowIcon,
  },
  sistema_operativo: {
    label: _t('Sistema operativo'),
    icon: ApplicationsIcon,
    showLabel: true,
    capitalized: true,
  },
  pannello: {
    label: _t('Pannello di controllo'),
    icon: VirtualMachineIcon,
  },
  servizio_email: {
    label: _t('Caselle email illimitate'),
    icon: EnvelopeIcon,
  },
  registrazione_dominio: {
    label: _t('Registrazione dominio'),
    icon: GlobeIcon,
  },
  database: {
    label: _t('Database'),
    icon: DatabaseIcon,
  },
  gestione_dns: {
    label: _t('Gestione DNS'),
    icon: NetworkIcon,
  },
  backup: {
    label: _t('Backup in location separata'),
    icon: ArrowsRotateIcon,
    tooltip: _t('Rotazione: 3 giornalieri, 2 settimanali, 4 mensili'),
  },
  support: {
    label: _t('Supporto'),
    icon: AddressCardIcon,
    capitalized: true,
    showLabel: true,
  },
  nr_domini: {
    label: _t('Multidominio'),
    icon: NetworkWiredIcon,
  },
  utenti: {
    label: _t('Utenti'),
    icon: UsersIcon,
    showLabel: true,
  },
  monitoring: {
    label: _t('Monitoraggio'),
    icon: EyeIcon,
    showLabel: true,
  },
  redirect: {
    label: _t('Redirect'),
    icon: ArrowIcon,
  },
  ssl_certificate: {
    label: _t('Certificato SSL gratuito'),
    icon: LockIcon,
  },
  ssh_access: {
    label: _t('Accesso SSH'),
    icon: LockIcon,
  },
  imunify360: {
    label: _t('Licenza Imunify360'),
    icon: LockIcon,
  },
  ddos_protection: {
    label: _t('Protezione DDOS'),
    icon: LockIcon,
  },
  waf: {
    label: _t('Web Application Firewall'),
    icon: LockIcon,
  },
  litespeed: {
    label: _t('Licenza LiteSpeed'),
    icon: GaugeIcon,
  },
  ttfb: {
    label: 'TTFB',
    icon: GaugeIcon,
    showLabel: true,
  },
  io: {
    label: 'IO',
    icon: GaugeIcon,
    um: 'MB/s',
    showLabel: true,
  },
  iops: {
    label: 'IOPS',
    icon: GaugeIcon,
    showLabel: true,
  },
  ep: {
    label: 'EP',
    icon: GaugeIcon,
    showLabel: true,
  },
  uptime: {
    label: 'Uptime',
    icon: GaugeIcon,
    um: '%',
    showLabel: true,
  },
  inode: {
    label: 'Inode',
    icon: GaugeIcon,
  },
});
</script>

<script lang="ts" setup>
import { $t as _t } from '@/i18n';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import type { CatalogProduct, ProductFeatures } from '@/resources';
import { PRODOTTIANAGRAFICA_MONITORING, PRODOTTIANAGRAFICA_SUPPORT, PRODUCTCATEGORY } from '../common/consts';

defineProps<{
  feature: keyof ProductFeatures;
  product: Pick<CatalogProduct, keyof typeof allFeatures | 'categoria' | 'codice' | 'group_code'>;
  showLabel?: boolean;
}>();

type Monitoring = {
  [K in (typeof PRODOTTIANAGRAFICA_MONITORING)[number]]: string;
};

type Support = {
  [K in (typeof PRODOTTIANAGRAFICA_SUPPORT)[number]]: string;
};

const monitoring: Monitoring = {
  basic: _t(
    'Monitoraggio essenziale che verifica costantemente i parametri vitali della macchina, come network e risorse hardware, garantendo una supervisione minima e affidabile dello stato generale del server.',
  ),
  full: _t(
    'Monitoraggio avanzato che, oltre ai parametri vitali, include un controllo continuo tramite sonde sullo stato dei principali servizi web hosting e mail, per assicurare affidabilità e performance costanti.',
  ),
  advanced: _t(
    'Monitoraggio completo con sonde dedicate e personalizzate che, oltre ai controlli standard, anticipano e prevengono eventuali criticità anche a livello applicativo.',
  ),
};

const support: Support = {
  unmanaged: _t('Nessun intervento diretto incluso; il cliente gestisce autonomamente e integralmente il servizio.'),
  basic: _t('Assistenza inclusa esclusivamente nella fase iniziale di avvio del servizio e supporto sistemistico di base.'),
  managed: _t(
    'Assistenza completa dalla fase di avvio del servizio, comprensiva di supporto tecnico continuativo negli orari lavorativi e aggiornamenti periodici per garantire la massima operatività e performance del servizio.',
  ),
};

const allFeatures = getAllProductFeatures();

function highlight(category: (typeof PRODUCTCATEGORY)[number], productCode: string, feature: keyof ProductFeatures) {
  const cloudHostingFeatures: (keyof ProductFeatures)[] = ['spazio'];
  const cloudResellerFeatures: (keyof ProductFeatures)[] = ['spazio', 'ram', 'core', 'support', 'utenti'];
  const titanFeatures: (keyof ProductFeatures)[] = ['spazio', 'traffico', 'ram', 'core', 'ip_dedicato'];

  return (
    (productCode === 'HOSTITA' && titanFeatures.includes(feature)) ||
    (category === 'Cloud Hosting' && cloudHostingFeatures.includes(feature)) ||
    (category === 'Cloud Reseller' && cloudResellerFeatures.includes(feature))
  );
}
</script>
